import { Component, HostListener, OnInit } from '@angular/core';
import { Router } from '@angular/router';

import { PortalApiService } from '../../portal-api.service';
import { PortalPreviewService } from '../../portal-preview.service';
import { PortalOverlayService } from '../portal-overlay.service';
import { portalDate } from '../portal-format';

interface NotifItem {
	type: string; unread: boolean; title: string; ref: string;
	project: string; project_id: number; route: string; datetime: string;
}

// One row of the customer's own activity: "You marked as to be confirmed —
// Quote #21655 — Additional Works". Server-built wording (portal_activity) so the
// staff-only rows in the shared activity table can never reach here.
interface ActivityItem {
	id: number; actor: string; you: boolean; initials: string; action: string;
	ref: string; title: string; project: string; project_id: number;
	route: string; datetime: string;
}

// Bell + notification panel — a dropdown on desktop, a centred popup on mobile
// (see .notif-panel in styles.scss). A single instance appears in every header;
// the feed is global (across all the customer's projects). Clicking an item
// navigates to the relevant project section.
@Component({
	selector: 'portal-notifications',
	template: `
		<button class="notif-bell" (click)="toggle($event)" aria-label="Notifications"
			[disabled]="preview.ready"
			[attr.title]="preview.ready ? preview.blockedReason : null">
			<i class="wq wq-alert"></i>
			<span class="notif-badge" *ngIf="unread">{{ unread > 9 ? '9+' : unread }}</span>
		</button>

		<div class="notif-overlay" [class.open]="open" (click)="close()">
			<div class="notif-panel" (click)="$event.stopPropagation()">
				<div class="notif-tabs">
					<button class="notif-tab" [class.active]="tab === 'notifications'" (click)="showTab('notifications')">Notifications</button>
					<button class="notif-tab" [class.active]="tab === 'activity'" (click)="showTab('activity')">Portal Activity</button>
					<button type="button" class="notif-close" (click)="close()" aria-label="Close"><i class="wq wq-close"></i></button>
				</div>

				<ng-container *ngIf="tab === 'notifications'">
					<div class="notif-header"><span class="notif-heading">Notifications</span></div>

					<div class="notif-pills">
						<button *ngFor="let p of pills" class="portal-pill compact" [class.active]="filter === p.key" (click)="filter = p.key">
							{{ p.label }} <span class="portal-pill-count">{{ p.count }}</span>
						</button>
					</div>

					<div class="notif-body">
						<p class="notif-empty" *ngIf="!loading && !filtered.length">You're all caught up.</p>
						<p class="notif-empty" *ngIf="loading">Loading…</p>

						<button class="notif-item" *ngFor="let n of filtered" (click)="openItem(n)">
							<span class="notif-icon" [ngClass]="'notif-icon-' + n.type">
								<i [class]="iconFor(n.type)"></i>
							</span>
							<span class="notif-content">
								<span class="notif-title-row">
									<span class="notif-title">{{ n.title }}</span>
									<span class="notif-dot" *ngIf="n.unread"></span>
								</span>
								<span class="notif-sub-row">
									<span class="notif-sub">
										<span>{{ n.ref }}</span>
										<span class="notif-sep" *ngIf="n.project">·</span>
										<span class="notif-proj" *ngIf="n.project">{{ n.project }}</span>
									</span>
									<span class="notif-time">{{ timeText(n.datetime) }}</span>
								</span>
							</span>
						</button>
					</div>
				</ng-container>

				<ng-container *ngIf="tab === 'activity'">
					<div class="notif-header"><span class="notif-heading">Portal Activity</span></div>

					<div class="notif-body">
						<p class="notif-empty" *ngIf="activityLoading">Loading…</p>
						<p class="notif-empty" *ngIf="!activityLoading && !activity.length">Nothing here yet.</p>

						<button class="notif-item" *ngFor="let a of activity" (click)="openActivity(a)">
							<span class="portal-avatar act-avatar">{{ a.initials }}</span>
							<span class="notif-content">
								<span class="act-title">{{ a.actor }} {{ a.action }} — {{ a.ref }}<ng-container *ngIf="a.title"> — {{ a.title }}</ng-container></span>
								<span class="act-meta">
									<span>{{ stampText(a.datetime) }}</span>
									<ng-container *ngIf="a.project">
										<span class="notif-sep">·</span>
										<span class="notif-proj">{{ a.project }}</span>
									</ng-container>
								</span>
							</span>
						</button>
					</div>
				</ng-container>
			</div>
		</div>
	`
})
export class NotificationsComponent implements OnInit {

	loading = false;
	items: NotifItem[] = [];
	counts: any = { all: 0, quote: 0, invoice: 0, accepted: 0 };
	unread = 0;
	filter = 'all';

	tab: 'notifications' | 'activity' = 'notifications';
	activity: ActivityItem[] = [];
	activityLoading = false;
	private activityLoaded = false;

	constructor(private api: PortalApiService, private router: Router, private overlay: PortalOverlayService, public preview: PortalPreviewService) {}

	get open(): boolean { return this.overlay.isOpen(this); }

	get pills(): { key: string; label: string; count: number }[] {
		return [
			{ key: 'all', label: 'All', count: this.counts.all || 0 },
			{ key: 'quote', label: 'Quotes', count: this.counts.quote || 0 },
			{ key: 'invoice', label: 'Invoices', count: this.counts.invoice || 0 },
			{ key: 'accepted', label: 'Accepted', count: this.counts.accepted || 0 }
		];
	}

	get filtered(): NotifItem[] {
		return this.filter === 'all' ? this.items : this.items.filter(i => i.type === this.filter);
	}

	ngOnInit(): void {
		// The bell is disabled in a preview, so the panel can never open and the feed would only be
		// fetched to be thrown away. A preview also has no feed of its own — these are a
		// stakeholder's own notifications, and a preview isn't a person.
		if (this.preview.ready) return;

		this.load();
	}

	private load(): void {
		this.loading = true;
		this.api.project.notifications((res: any) => {
			this.items = res?.notifications || [];
			this.counts = res?.counts || this.counts;
			this.unread = res?.unread || 0;
			this.loading = false;
		}, () => { this.loading = false; });
	}

	toggle(event: MouseEvent): void {
		event.stopPropagation();
		this.overlay.toggle(this);
	}

	close(): void { this.overlay.close(this); }

	@HostListener('document:keydown.escape')
	onEsc(): void { this.overlay.close(this); }

	// The activity feed is fetched the first time its tab is opened, not on load —
	// most visits never look at it.
	showTab(tab: 'notifications' | 'activity'): void {
		this.tab = tab;
		if (tab === 'activity' && !this.activityLoaded) this.loadActivity();
	}

	private loadActivity(): void {
		this.activityLoaded = true;
		this.activityLoading = true;
		this.api.project.activity((res: any) => {
			this.activity = res?.activity || [];
			this.activityLoading = false;
		}, () => {
			// Let it retry next time the tab is opened.
			this.activityLoaded = false;
			this.activityLoading = false;
		});
	}

	openItem(n: NotifItem): void {
		this.close();
		this.router.navigate(['/project', n.project_id, n.route]);
	}

	openActivity(a: ActivityItem): void {
		this.close();
		this.router.navigate(['/project', a.project_id, a.route]);
	}

	iconFor(type: string): string {
		if (type === 'invoice') return 'wq wq-invoice';
		if (type === 'accepted') return 'wq wq-handshake';
		return 'wq wq-handshake';
	}

	// Activity rows are a log, so they carry the actual moment ("13 August 2026 ·
	// 2:05pm") rather than the relative "2h ago" the notification list uses.
	stampText(datetime: string): string {
		if (!datetime) return '';
		// MySQL hands back "2026-08-13 14:05:00"; normalise the separator so the date
		// parses everywhere, as timeText() below already does.
		const iso = datetime.replace(' ', 'T');
		const date = portalDate(iso, 'longDate');
		const time = portalDate(iso, 'shortTime');
		return time ? `${date} · ${time}` : date;
	}

	timeText(datetime: string): string {
		if (!datetime) return '';
		const d = new Date(datetime.replace(' ', 'T'));
		if (isNaN(d.getTime())) return datetime;
		const diff = Date.now() - d.getTime();
		const day = 86400000;
		if (diff < 0) return portalDate(datetime);
		if (diff < 3600000) return `${Math.max(1, Math.round(diff / 60000))} min ago`;
		if (diff < day) return `${Math.round(diff / 3600000)}h ago`;
		if (diff < 2 * day) return 'Yesterday';
		if (diff < 7 * day) return `${Math.round(diff / day)}d ago`;
		return portalDate(datetime);
	}
}
